import React, { Component } from "react";
import axios from "axios";
import { BASE_URL } from "../../_constants/urlConstants";
import { userService } from "../../_services/userService";
import "./styles/qSheetPrint.css";

const IMAGE_TYPES = new Set([
  "image/jpeg", "image/jpg", "image/png", "image/gif",
  "image/bmp", "image/webp", "image/svg+xml",
]);

const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"]);

function isImageAttachment(att) {
  if (att.contentType && IMAGE_TYPES.has(att.contentType.toLowerCase())) return true;
  const ext = (att.fileName ?? "").split(".").pop().toLowerCase();
  return IMAGE_EXTS.has(ext);
}

function fmt3(n) {
  return parseFloat((n ?? 0).toFixed(3)).toString();
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric", month: "2-digit", day: "2-digit",
  });
}

function fmtBytes(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

class QSheetPrintView extends Component {
  state = {
    qSheet: null,
    error: null,
    blobUrls: {},   // attachmentId → object URL
    imagesLoaded: false,
  };

  _blobUrls = {};

  componentDidMount() {
    userService.setAuthHeader();
    const { id } = this.props.match.params;
    axios
      .get(`${BASE_URL}/api/QSheets/${id}`)
      .then((res) => {
        this.setState({ qSheet: res.data }, () => this._loadImages());
      })
      .catch(() => this.setState({ error: "Failed to load QSheet data." }));
  }

  componentWillUnmount() {
    Object.values(this._blobUrls).forEach((url) => URL.revokeObjectURL(url));
  }

  _loadImages() {
    const { qSheet } = this.state;
    const imageAtts = (qSheet?.attachments ?? []).filter(isImageAttachment);

    if (imageAtts.length === 0) {
      this.setState({ imagesLoaded: true }, () => window.print());
      return;
    }

    const { id } = this.props.match.params;
    const promises = imageAtts.map((att) =>
      axios
        .get(`${BASE_URL}/api/QSheets/${id}/Attachments/${att.qSheetAttachmentId}`, {
          responseType: "blob",
        })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          this._blobUrls[att.qSheetAttachmentId] = url;
          return { id: att.qSheetAttachmentId, url };
        })
        .catch(() => ({ id: att.qSheetAttachmentId, url: null }))
    );

    Promise.all(promises).then((results) => {
      const blobUrls = {};
      results.forEach(({ id: aid, url }) => { if (url) blobUrls[aid] = url; });
      this.setState({ blobUrls, imagesLoaded: true }, () => {
        // Give browser one tick to paint images before printing
        setTimeout(() => window.print(), 100);
      });
    });
  }

  render() {
    const { qSheet, error, blobUrls, imagesLoaded } = this.state;

    if (error) return <div className="qs-print-error">{error}</div>;
    if (!qSheet) return <div className="qs-print-loading">Loading…</div>;

    const unit             = qSheet.unitOfMeasure ?? "";
    const eeQty            = qSheet.bidItemEeQuantity ?? 0;
    const ccoQty           = qSheet.ccoQuantity ?? 0;
    const totalAuthQty     = qSheet.totalAuthorizedQuantity ?? 0;
    const prevPaid         = qSheet.previouslyPaidQuantity ?? 0;

    const drSum   = (qSheet.rows ?? []).reduce((s, r) => s + (parseFloat(r.estimatedQuantity) || 0), 0);
    const adjSum  = (qSheet.adjustments ?? []).reduce((s, a) => s + (parseFloat(a.adjustmentAmount) || 0), 0);
    const payThis = drSum + adjSum;
    const totalToDate = prevPaid + payThis;

    const overrun = totalAuthQty > 0 && totalToDate > totalAuthQty * 1.25;

    const imageAtts    = (qSheet.attachments ?? []).filter(isImageAttachment);
    const nonImageAtts = (qSheet.attachments ?? []).filter((a) => !isImageAttachment(a));

    const sheetNumber  = qSheet.sheetNumber != null ? qSheet.sheetNumber : "(Draft)";
    const status       = qSheet.statusLabel ?? "Draft";
    const fileNo     = qSheet.fileNo ?? "";
    const txType       = qSheet.intendedTransactionType;
    const txTypeLabel  = {
      ContractItemPayment:           "Contract Item Payment",
      ContractItemQuantityBalance:   "Contract Item Quantity Balance (Q)",
      ContractItemAnticipatedChange: "Contract Item Anticipated Change (A)",
      ContractItemFinalBalance:      "Contract Item Final Balance (F)",
    }[txType] ?? txType ?? "";
    const isStructures = qSheet.isStructuresItem ?? false;
    const isSegregated = qSheet.isSegregated ?? false;
    const hasCem6004   = qSheet.cem6004PageNumber || qSheet.cem6004LineNumber || qSheet.cem6004PostingDate;

    return (
      <div className="qs-print-root">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="qs-print-header">
          <div className="qs-print-title-row">
            <h2>Quantity Calculations</h2>
            <span className="qs-print-status">{status}</span>
            {isStructures && <span className="qs-print-status qs-print-badge-structures">Structures Item</span>}
            {isSegregated && <span className="qs-print-status qs-print-badge-segregated">Segregated</span>}
          </div>
          <table className="qs-print-meta-table">
            <tbody>
              <tr>
                <th>Pay Estimate #:</th>
                <td>{qSheet.estimateNumber ?? ""}</td>
                <th>Pay Period:</th>
                <td>{fmtDate(qSheet.payPeriodStartDate)} – {fmtDate(qSheet.payPeriodEndDate)}</td>
              </tr>
              <tr>
                <th>EA:</th>
                <td>{qSheet.projectEA ?? ""}</td>
                <th>File No:</th>
                <td>{fileNo}</td>
              </tr>
              <tr>
                <th>Item:</th>
                <td colSpan={3}>{qSheet.itemNo ? `Item ${qSheet.itemNo}` : ""}{qSheet.itemNo && qSheet.description ? " – " : ""}{qSheet.description ?? ""}</td>
              </tr>
              <tr>
                <th>Calculated By:</th>
                <td>{qSheet.calculatedByFullName ?? ""}</td>
                <th>Calculated On:</th>
                <td>{fmtDate(qSheet.calculatedOn)}</td>
              </tr>
              <tr>
                <th>Checked By:</th>
                <td>{qSheet.checkedByFullName ?? ""}</td>
                <th>Checked On:</th>
                <td>{fmtDate(qSheet.checkedOn)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr className="qs-print-divider" />

        {/* ── Daily Report Rows ───────────────────────────────────────── */}
        <h4>Daily Report Quantities</h4>
        <table className="qs-print-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Report #</th>
              <th>Author</th>
              <th>Location</th>
              <th className="text-right">Qty ({unit})</th>
            </tr>
          </thead>
          <tbody>
            {(qSheet.rows ?? []).length === 0 ? (
              <tr><td colSpan={5} className="text-center text-muted">No rows</td></tr>
            ) : (
              (qSheet.rows ?? []).map((row, i) => (
                <tr key={i}>
                  <td>{fmtDate(row.reportDate)}</td>
                  <td>{row.reportNumber ?? ""}</td>
                  <td>{row.author ?? ""}</td>
                  <td>{row.locationName ?? ""}</td>
                  <td className="text-right">{fmt3(parseFloat(row.estimatedQuantity) || 0)}</td>
                </tr>
              ))
            )}
            <tr className="qs-print-subtotal-row">
              <td colSpan={4}><b>Subtotal DR Quantities</b></td>
              <td className="text-right"><b>{fmt3(drSum)}</b></td>
            </tr>
          </tbody>
        </table>

        {/* ── Adjustments ────────────────────────────────────────────── */}
        {(qSheet.adjustments ?? []).length > 0 && (
          <>
            <h4 className="mt-3">Adjustments</h4>
            <table className="qs-print-table">
              <thead>
                <tr>
                  <th>Justification</th>
                  <th className="text-right">Amount ({unit})</th>
                </tr>
              </thead>
              <tbody>
                {(qSheet.adjustments ?? []).map((adj, i) => (
                  <tr key={i}>
                    <td>{adj.justification ?? ""}</td>
                    <td className="text-right">{fmt3(parseFloat(adj.adjustmentAmount) || 0)}</td>
                  </tr>
                ))}
                <tr className="qs-print-subtotal-row">
                  <td><b>Total Adjustments</b></td>
                  <td className="text-right"><b>{fmt3(adjSum)}</b></td>
                </tr>
              </tbody>
            </table>
          </>
        )}

        <hr className="qs-print-divider" />

        {/* ── Totals ─────────────────────────────────────────────────── */}
        <h4>Totals</h4>
        <div className="qs-print-totals-columns">
          {/* Column 1: Authorized quantities */}
          <table className="qs-print-totals-table">
            <tbody>
              <tr>
                <th>Bid Item Qty (EE):</th>
                <td>{fmt3(eeQty)} {unit}</td>
              </tr>
              <tr>
                <th>CCO Qty:</th>
                <td>{fmt3(ccoQty)} {unit}</td>
              </tr>
              <tr>
                <th><b>Total Authorized Qty:</b></th>
                <td><b>{fmt3(totalAuthQty)} {unit}</b></td>
              </tr>
            </tbody>
          </table>

          {/* Column 2: Payment summary */}
          <table className="qs-print-totals-table">
            <tbody>
              <tr className="qs-print-pay-row">
                <th>Pay This Estimate:</th>
                <td>{fmt3(payThis)} {unit}</td>
              </tr>
              <tr>
                <th>Previously Paid:</th>
                <td>{fmt3(prevPaid)} {unit}</td>
              </tr>
              <tr>
                <th><b>Total to Date:</b></th>
                <td><b>{fmt3(totalToDate)} {unit}</b></td>
              </tr>
              {overrun && (
                <tr className="qs-print-overrun-row">
                  <td colSpan={2}><b>OVERRUN:</b> Total to Date exceeds 125% of authorized quantity.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Notes / Remarks ────────────────────────────────────────── */}
        {qSheet.notesRemarks && (
          <>
            <hr className="qs-print-divider" />
            <h4>Notes / Remarks</h4>
            <p className="qs-print-notes">{qSheet.notesRemarks}</p>
          </>
        )}

        {/* ── CEM-6004 Cross-Reference ────────────────────────────────── */}
        {hasCem6004 && (
          <>
            <hr className="qs-print-divider" />
            <h4>CEM-6004 Cross-Reference</h4>
            <table className="qs-print-totals-table">
              <tbody>
                <tr>
                  <th>Page #:</th>
                  <td>{qSheet.cem6004PageNumber ?? ""}</td>
                </tr>
                <tr>
                  <th>Line #:</th>
                  <td>{qSheet.cem6004LineNumber ?? ""}</td>
                </tr>
                <tr>
                  <th>Posting Date:</th>
                  <td>{fmtDate(qSheet.cem6004PostingDate)}</td>
                </tr>
                {qSheet.cem6004RecordedByFullName && (
                  <tr>
                    <th>Recorded By:</th>
                    <td>{qSheet.cem6004RecordedByFullName}{qSheet.cem6004RecordedOn ? ` on ${fmtDate(qSheet.cem6004RecordedOn)}` : ""}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {/* ── Image Attachments ───────────────────────────────────────── */}
        {imageAtts.length > 0 && (
          <>
            <hr className="qs-print-divider" />
            <h4>Attachments</h4>
            {!imagesLoaded && <p className="text-muted">Loading images…</p>}
            {imageAtts.map((att) => (
              <div key={att.qSheetAttachmentId} className="qs-print-image-block">
                <p className="qs-print-image-caption">{att.fileName}</p>
                {blobUrls[att.qSheetAttachmentId] && (
                  <img
                    src={blobUrls[att.qSheetAttachmentId]}
                    alt={att.fileName}
                    className="qs-print-image"
                  />
                )}
              </div>
            ))}
          </>
        )}

        {/* ── Non-image Attachments ───────────────────────────────────── */}
        {nonImageAtts.length > 0 && (
          <>
            <hr className="qs-print-divider" />
            <h4>Reference Documents</h4>
            <table className="qs-print-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Size</th>
                  <th>Uploaded By</th>
                  <th>Upload Date</th>
                </tr>
              </thead>
              <tbody>
                {nonImageAtts.map((att) => (
                  <tr key={att.qSheetAttachmentId}>
                    <td>{att.fileName}</td>
                    <td>{fmtBytes(att.fileSizeBytes)}</td>
                    <td>{att.uploadedByFullName ?? ""}</td>
                    <td>{fmtDate(att.uploadedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

      </div>
    );
  }
}

export default QSheetPrintView;
